import {
    Box,
    Divider,
    DropdownContent,
    DropdownMenu,
    DropdownMenuItem,
    DropdownTrigger,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@bcc-ui/ui-components";
import classNames from "classnames";
import { useFeature } from "flagged";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps, useDispatch, useSelector } from "react-redux";
import { Link, useHistory, useLocation } from "react-router-dom";

import { MainNavigationPanel } from "./main-navigation-panel/MainNavigationPanel";
import style from "./navigation-panel.scss";
import SubNavigationPanel from "./sub-navigation-panel/SubNavigationPanel";
import Premium from "components/icons/Premium";
import { SidebarToggle } from "components/icons/SidebarToggle";
import { SwitchApplication } from "components/icons/SwitchApplication";
import { Logo } from "components/logo/Logo";
import NavigationMenu from "components/navigation/navigation-menu/NavigationMenu";
import AssetsSummaryFooter from "components/navigation/navigation-panel/AssetsSummaryFooter";
import { NavigationPanelFooter } from "components/navigation/navigation-panel/navigation-panel-footer/NavigationPanelFooter";
import { detectApplicationFromPath, HOME_ROUTE, INTEGRATIONS_ROUTE } from "components/router/Routes";
import { Breakpoint } from "domain/breakpoints";
import {
    FLAG_ASSET_MANAGEMENT,
    FLAG_BAM_PORTAL,
    FLAG_MOBILE_NAVIGATION,
    FLAG_RESTRUCTURED_NAVIGATION,
} from "services/feature/FeatureFlagService";
import { tenantHasFeatureLicense } from "services/tenants/tenantCookieService";
import { StoreState } from "store";
import { Application, setApplication } from "store/application";
import { resolveColor } from "utils/commonFunctions";
import { getObject, RepositoryKey, setObject } from "utils/repository";
import { useIsDarkThemeActive } from "utils/useTheme";

import testIds from "testIds.json";

interface Props {
    onNavigationPanelToggle: (value: boolean) => void;
    onSubNavigationOpenChange: (value: boolean) => void;
    isSubNavigationOpened: boolean;
    isMobileNavigationOpen: boolean;
    isMobileMainNavigationOpen: boolean;
    isTenantBannerVisible: boolean;
    isTrialTenant?: boolean;
}

const connector = connect((state: StoreState) => ({
    user: state.userReducer.user,
    tenantDetails: state.tenantDetailsReducer.stack[state.tenantDetailsReducer.stack.length - 1],
    theme: state.themeReducer.theme,
}));

const NavigationPanel = (props: Props & ConnectedProps<typeof connector>): JSX.Element => {
    const { t } = useTranslation();
    const navigationPanelRef = React.useRef<HTMLDivElement>(null);
    const navigationHeaderRef = React.useRef<HTMLDivElement>(null);
    const navigationContentRef = React.useRef<HTMLDivElement>(null);

    const dispatch = useDispatch();
    const application = useSelector((state: StoreState) => state.applicationReducer.selected);
    const history = useHistory();
    const { pathname } = useLocation();
    const isAssetManagementFlagActive = useFeature(FLAG_ASSET_MANAGEMENT);
    const isBamPortalFlagActive = useFeature(FLAG_BAM_PORTAL);
    const isRestructuredNavPanelActive = useFeature(FLAG_RESTRUCTURED_NAVIGATION);

    // TODO BCC-7524: Remove when mobile navigation feature is complete:
    const isMobileNavigationFlagActive = useFeature(FLAG_MOBILE_NAVIGATION);
    const isDarkMode = useIsDarkThemeActive();

    const [isMinimized, setIsMinimized] = React.useState(getObject(RepositoryKey.SIDE_NAVIGATION) === false);

    const [isResizing, setIsResizing] = React.useState(false);
    const [isKebabOpened, setIsKebabOpened] = React.useState(false);
    const isKebabOpenedRef = React.useRef(false);

    React.useEffect(() => {
        isKebabOpenedRef.current = isKebabOpened;
        if (!isKebabOpened) {
            const panel = navigationPanelRef.current;
            if (panel && !panel.matches(":hover")) {
                panel.classList.remove("minimizedPanelOpen");
            }
        }
    }, [isKebabOpened]);

    React.useEffect(() => {
        const content = navigationContentRef.current;
        if (!content || !isKebabOpened) {
            return;
        }
        const blockWheel = (event: WheelEvent) => {
            event.preventDefault();
        };
        const closeKebabOnScroll = () => {
            document.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", bubbles: true, cancelable: true }));
        };
        content.addEventListener("wheel", blockWheel, { passive: false });
        content.addEventListener("scroll", closeKebabOnScroll);
        return () => {
            content.removeEventListener("wheel", blockWheel);
            content.removeEventListener("scroll", closeKebabOnScroll);
        };
    }, [isKebabOpened]);

    function isBamAvailable() {
        const hasFeatureLicense = tenantHasFeatureLicense("FEATURE_ASSET_MANAGEMENT");
        return isAssetManagementFlagActive && hasFeatureLicense;
    }

    const [windowSize, setWindowSize] = React.useState({
        width: window.innerWidth,
    });

    const toggleNavigation = () => {
        // Prevent hover events when the resizing is running.
        // Otherwise the resizing from maximized from minimized will be disturbed, and the layout doesn't look correct.
        setIsResizing(true);
        setIsMinimized((prevState) => !prevState);
    };

    React.useEffect(() => {
        const handleResize = () => {
            setWindowSize({
                width: window.innerWidth,
            });
        };

        window.addEventListener("resize", handleResize);

        if (getObject(RepositoryKey.SIDE_NAVIGATION) != null) {
            if (getObject(RepositoryKey.SIDE_NAVIGATION) == true) {
                setIsMinimized(false);
            }
            if (getObject(RepositoryKey.SIDE_NAVIGATION) == false) {
                setIsMinimized(true);
            }
        } else {
            if (windowSize.width <= Breakpoint.XL) {
                setIsMinimized(true);
            } else {
                if (windowSize.width > Breakpoint.XL) {
                    setIsMinimized(false);
                }
            }
        }

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [windowSize.width]);

    React.useEffect(() => {
        setObject(RepositoryKey.SIDE_NAVIGATION, !isMinimized);
        props.onNavigationPanelToggle(isMinimized);
    }, [isMinimized]);

    function isOnBam() {
        return application === Application.BAM;
    }

    React.useEffect(() => {
        const panelElement = navigationPanelRef.current;
        const headerElement = navigationHeaderRef.current;
        const contentElement = navigationContentRef.current;

        if (!panelElement || !headerElement || !contentElement || isResizing) {
            return;
        }

        const handleAnimationEnd = () => {
            setIsResizing(false);
            // Notify any react-grid-layouts about the resized layout.
            window.dispatchEvent(new Event("resize"));
        };

        const handleMouseEnter = () => {
            panelElement.classList.add("minimizedPanelOpen");
        };

        const handleMouseExit = () => {
            if (!isKebabOpenedRef.current) {
                panelElement.classList.remove("minimizedPanelOpen");
            }
        };

        panelElement.addEventListener("transitionend", handleAnimationEnd);

        // Open the minimized navigation only when hovering either the navigation header or content.
        headerElement.addEventListener("mouseenter", handleMouseEnter);
        contentElement.addEventListener("mouseenter", handleMouseEnter);

        // Clear the hover class when the hover exists navigation panel.
        panelElement.addEventListener("mouseleave", handleMouseExit);

        // TODO BCC-5887 How to check if there is no BMP access?
        if (!isBamAvailable() && isOnBam()) {
            handleSetApplication(Application.BMP);
        }

        return () => {
            panelElement.removeEventListener("transitionend", handleAnimationEnd);
            headerElement.removeEventListener("mouseenter", handleMouseEnter);
            contentElement.removeEventListener("mouseenter", handleMouseEnter);
            panelElement.removeEventListener("mouseleave", handleMouseExit);
        };
    }, []);

    React.useEffect(() => {
        const currentApplication = detectApplicationFromPath(pathname);
        if (currentApplication !== undefined && currentApplication !== application) {
            if (currentApplication === Application.BAM && !isBamAvailable()) {
                return;
            }
            // In BMP+assets mode the asset routes are embedded in BMP navigation,
            // so navigating to /assets/* should not switch the app to BAM.
            if (
                currentApplication === Application.BAM &&
                application === Application.BMP &&
                isAssetManagementFlagActive
            ) {
                return;
            }
            dispatch(setApplication(currentApplication));
        }
    }, [pathname, isAssetManagementFlagActive]);

    const handleSetApplication = (application: Application) => {
        dispatch(setApplication(application));
        if (application === Application.BAM) {
            history.push(INTEGRATIONS_ROUTE.path);
        } else {
            history.push(HOME_ROUTE.path);
        }
    };

    function displayAssetsSummary() {
        if (!isOnBam()) {
            return null;
        }
        return (
            <Box columnLayout>
                <Divider />
                <AssetsSummaryFooter
                    minimized={isRestructuredNavPanelActive ? !props.isSubNavigationOpened : isMinimized}
                />
            </Box>
        );
    }

    return (
        <>
            {/* TODO: BCC-7010: remove isRestructuredNavPanelActive condition  when the RMN is done */}
            {isRestructuredNavPanelActive ? (
                <Box className={classNames(style.restructuredNavigationPanel)}>
                    <MainNavigationPanel
                        onSubNavigationOpenChange={props.onSubNavigationOpenChange}
                        isSubNavigationOpened={props.isSubNavigationOpened}
                        isMobileMainNavigationOpen={props.isMobileMainNavigationOpen}
                    />

                    <SubNavigationPanel
                        onSubNavigationOpenChange={props.onSubNavigationOpenChange}
                        isSubNavigationOpened={props.isSubNavigationOpened}
                        isDarkMode={isDarkMode}
                    />
                </Box>
            ) : (
                <div
                    id="navigationPanel"
                    className={classNames(style.navigationPanel, {
                        ["minimizedPanel"]: isMinimized,
                        [style.preventHoverActions]: isResizing,
                        [style.mobileOpen]: props.isMobileNavigationOpen && isMobileNavigationFlagActive,
                        ["tenantBannerVisible"]: props.isTenantBannerVisible,
                        // TODO BCC-7524: Remove this line and related css from layout.css on feature flag removal:
                        ["mobileNavigationFlagActive"]: isMobileNavigationFlagActive,
                    })}
                    ref={navigationPanelRef}
                >
                    <div className={style.navigationPanelHeader} ref={navigationHeaderRef}>
                        {isBamPortalFlagActive && isBamAvailable() ? (
                            <DropdownMenu position={"bottom-start"}>
                                <DropdownTrigger testId={testIds.navigation.productSwitch.switchButton}>
                                    <Tooltip placement={"bottom"}>
                                        <TooltipContent testId={testIds.navigation.productSwitch.switchButtonTooltip}>
                                            {t("Routes.switchProductTooltip")}
                                        </TooltipContent>
                                        <TooltipTrigger>
                                            <SwitchApplication
                                                borderColor={resolveColor("--app-navigationPanel-iconColor")}
                                            />
                                        </TooltipTrigger>
                                    </Tooltip>
                                </DropdownTrigger>
                                <DropdownContent testId={testIds.navigation.productSwitch.dropdownMenu.itself}>
                                    <DropdownMenuItem
                                        onClick={() => handleSetApplication(Application.BMP)}
                                        testId={testIds.navigation.productSwitch.dropdownMenu.bmpMenuItem}
                                        className={style.dropDownMenu}
                                    >
                                        <div className={style.dropDownMenu}>
                                            {t("Routes.bmpProduct")}
                                            <Premium />
                                        </div>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                        onClick={() => handleSetApplication(Application.BAM)}
                                        testId={testIds.navigation.productSwitch.dropdownMenu.bamMenuItem}
                                        className={style.dropDownMenu}
                                    >
                                        {t("Routes.bamProduct")}
                                    </DropdownMenuItem>
                                </DropdownContent>
                            </DropdownMenu>
                        ) : null}
                        <div className={style.logoContainer}>
                            <Link to="/" test-id={testIds.navigation.togglePanel.defaultLink}>
                                <Logo className={style.maxLogo} isWhiteLogo={!isOnBam() || isDarkMode} />
                            </Link>
                        </div>
                        <button
                            className={style.toggleButton}
                            onClick={toggleNavigation}
                            data-testid={testIds.navigation.togglePanel.button}
                        >
                            <SidebarToggle
                                color={
                                    isOnBam()
                                        ? resolveColor("--app-navigationPanel-iconColor")
                                        : resolveColor("--theme-icon-fill-white")
                                }
                                rotate={isMinimized}
                            />
                        </button>
                    </div>

                    <div className={style.navigationPanelContent} ref={navigationContentRef}>
                        <NavigationMenu onKebabOpenChange={setIsKebabOpened} />
                    </div>

                    {displayAssetsSummary()}
                    <div className={style.navigationPanelFooter}>
                        <NavigationPanelFooter
                            isOnBam={isOnBam()}
                            isMinimized={isMinimized}
                            className={props.isTrialTenant ? style.footerLayoutWithTrialTenant : style.footerLayout}
                        />
                    </div>
                </div>
            )}
        </>
    );
};

const connectedSideNavigation = connector(NavigationPanel);
export { connectedSideNavigation as NavigationPanel };
